import React from "react";
import { SubscriberFilters } from "../types/newsletterEdition";
import DateRangePicker from "./DateRangePicker";

interface SubscriberFiltersProps {
  filters: SubscriberFilters;
  onFiltersChange: (filters: SubscriberFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

const SubscriberFiltersComponent: React.FC<SubscriberFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const statusOptions = [
    { value: "Active", label: "Active", color: "#44AD54" },
    { value: "Inactive", label: "Inactive", color: "#F59E0B" },
    { value: "Unsubscribed", label: "Unsubscribed", color: "#718096" },
    { value: "Bounced", label: "Bounced", color: "#E53E3E" },
  ];

  const handleStatusChange = (status: string) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];

    onFiltersChange({ ...filters, status: newStatuses });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, search: e.target.value });
  };

  const handleDateRangeChange = (dateFrom: string | undefined, dateTo: string | undefined) => {
    onFiltersChange({ ...filters, dateFrom, dateTo });
  };

  const hasActiveFilters = () => {
    return (
      (filters.status && filters.status.length > 0) ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.search
    );
  };

  return (
    <div style={{
      marginBottom: "20px",
      padding: "16px",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      backgroundColor: "#fff",
    }}>
      {/* Single row layout with flex wrap for responsive design */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        alignItems: "center",
      }}>
        {/* Search input */}
        <div style={{
          flex: "1 1 200px",
          minWidth: "150px",
        }}>
          <input
            type="text"
            value={filters.search || ""}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onApplyFilters();
              }
            }}
            placeholder="Search by email..."
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
        </div>

        {/* Status selector with dropdown */}
        <div style={{
          position: "relative",
          flex: "0 1 auto",
        }}>
          <StatusMultiSelect
            options={statusOptions}
            selectedValues={filters.status || []}
            onChange={(newStatuses) => onFiltersChange({ ...filters, status: newStatuses })}
          />
        </div>

        {/* Date range picker */}
        <div style={{
          flex: "0 1 auto",
        }}>
          <DateRangePicker
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            onChange={handleDateRangeChange}
            placeholder="Select date range"
          />
        </div>

        {/* Action buttons */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          flex: "0 1 auto",
        }}>
          <button
            onClick={onApplyFilters}
            className="primary-button"
            
            style={{
              padding: "auto auto",
              height: "fit-content",
            }}
          >
            Apply
          </button>
          <button
            onClick={onClearFilters}
            disabled={!hasActiveFilters()}
            className="primary-button"
            style={{
              padding: "auto auto",
              height: "fit-content",
              backgroundColor: "#5D5DED",
              borderColor: "#5D5DED",
            }}
          >
            Clear
          </button>
          
        </div>
      </div>

      {/* Active filters indicator */}
      {hasActiveFilters() && (
        <div style={{
          marginTop: "12px",
          fontSize: "13px",
          color: "#666",
        }}>
          <span style={{ fontWeight: "500" }}>Active filters: </span>
          {filters.search && (
            <span style={{
              display: "inline-block",
              padding: "2px 8px",
              margin: "0 4px",
              backgroundColor: "#f0f4ff",
              borderRadius: "12px",
              fontSize: "12px",
            }}>
              Search: {filters.search}
            </span>
          )}
          {filters.status && filters.status.map((status) => {
            const option = statusOptions.find(o => o.value === status);
            return (
              <span
                key={status}
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  margin: "0 4px",
                  backgroundColor: "#f0f4ff",
                  borderRadius: "12px",
                  fontSize: "12px",
                  color: option?.color,
                }}
              >
                {status}
              </span>
            );
          })}
          {(filters.dateFrom || filters.dateTo) && (
            <span style={{
              display: "inline-block",
              padding: "2px 8px",
              margin: "0 4px",
              backgroundColor: "#f0f4ff",
              borderRadius: "12px",
              fontSize: "12px",
            }}>
              Date: {filters.dateFrom || '...'} to {filters.dateTo || '...'}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Status multi-select dropdown component
interface StatusMultiSelectProps {
  options: Array<{ value: string; label: string; color: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
}

const StatusMultiSelect: React.FC<StatusMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const getButtonText = () => {
    if (selectedValues.length === 0) return "All Statuses";
    if (selectedValues.length === 1) {
      const option = options.find(o => o.value === selectedValues[0]);
      return option?.label || "Status";
    }
    return `${selectedValues.length} Statuses`;
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: "8px 12px",
          border: "1px solid #e2e8f0",
          borderRadius: "4px",
          backgroundColor: "#fff",
          cursor: "pointer",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          minWidth: "140px",
          justifyContent: "space-between",
        }}
      >
        <span style={{
          color: selectedValues.length > 0 ? "#333" : "#999",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
          {getButtonText()}
        </span>
        <span style={{ fontSize: "10px", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            minWidth: "200px",
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            padding: "8px",
          }}
        >
          {options.map((option) => (
            <label
              key={option.value}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "6px 8px",
                borderRadius: "4px",
                cursor: "pointer",
                backgroundColor: selectedValues.includes(option.value) ? "#f0f4ff" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (!selectedValues.includes(option.value)) {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedValues.includes(option.value)) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                style={{
                  marginRight: "8px",
                  cursor: "pointer",
                }}
              />
              <span style={{ color: option.color, fontSize: "14px", fontWeight: "500" }}>
                {option.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubscriberFiltersComponent;